import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { ModalShell, Field, TextInput, SelectInput, ModalFooter, ErrorBanner } from "../../shared/FormElements";

const STAGES = ["lead", "mql", "sql", "meeting", "proposal", "negotiation", "won", "lost"];
const STAGE_LABELS = {
  lead: "Lead",
  mql: "MQL",
  sql: "SQL",
  meeting: "Meeting",
  proposal: "Proposal",
  negotiation: "Negotiation",
  won: "Won",
  lost: "Lost",
};
const FUNNEL_STAGES = ["lead", "mql", "meeting", "proposal", "negotiation", "won"];

export default function SalesPipeline() {
  const [deals, setDeals] = useState([]);
  const [clients, setClients] = useState([]);
  const [revenueTarget, setRevenueTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const [dealsRes, clientsRes, financeRes] = await Promise.all([
      supabase.from("deals").select("*, clients(company_name)").order("created_at", { ascending: false }),
      supabase.from("clients").select("id, company_name").order("company_name"),
      supabase.from("financial_entries").select("revenue_target").order("month", { ascending: false }).limit(1).single(),
    ]);
    setDeals(dealsRes.data || []);
    setClients(clientsRes.data || []);
    setRevenueTarget(financeRes.data?.revenue_target || null);
    setLoading(false);
  }

  const stats = useMemo(() => {
    const byStage = (s) => deals.filter((d) => d.stage === s);
    const won = byStage("won");
    const lost = byStage("lost");
    const open = deals.filter((d) => !["won", "lost"].includes(d.stage));
    const pipelineValue = open.reduce((sum, d) => sum + Number(d.value), 0);

    const weights = { lead: 0.1, mql: 0.2, sql: 0.3, meeting: 0.4, proposal: 0.6, negotiation: 0.8 };
    const weightedPipeline = open.reduce((sum, d) => sum + Number(d.value) * (weights[d.stage] || 0.1), 0);

    const allDealValues = deals.filter((d) => d.stage !== "lost").map((d) => Number(d.value));
    const avgDealSize = allDealValues.length ? allDealValues.reduce((a, b) => a + b, 0) / allDealValues.length : 0;

    const winRate = won.length + lost.length > 0 ? Math.round((won.length / (won.length + lost.length)) * 100) : 0;

    const cycleDays = won
      .filter((d) => d.expected_close_date)
      .map((d) => (new Date(d.expected_close_date) - new Date(d.created_at)) / (1000 * 60 * 60 * 24));
    const avgCycle = cycleDays.length ? Math.round(cycleDays.reduce((a, b) => a + b, 0) / cycleDays.length) : null;

    const thisMonth = new Date().toISOString().slice(0, 7);
    const newMrr = won
      .filter((d) => d.created_at?.slice(0, 7) === thisMonth)
      .reduce((sum, d) => sum + Number(d.value), 0);

    const pipelineCoverage = revenueTarget ? Math.round((pipelineValue / revenueTarget) * 100) : null;

    return {
      newLeads: byStage("lead").length,
      mqls: byStage("mql").length,
      sqls: byStage("sql").length,
      meetings: byStage("meeting").length,
      proposals: byStage("proposal").length,
      won: won.length,
      lost: lost.length,
      winRate,
      pipelineValue,
      weightedPipeline,
      avgDealSize,
      avgCycle,
      newMrr,
      pipelineCoverage,
    };
  }, [deals, revenueTarget]);

  const funnelCounts = FUNNEL_STAGES.map((s) => deals.filter((d) => d.stage === s).length);
  const maxFunnel = Math.max(...funnelCounts, 1);

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">Sales &amp; pipeline</h1>
      <p className="text-sm text-muted-foreground mb-5">Manual deal entry</p>

      <div className="grid grid-cols-4 gap-3 mb-5">
        <Kpi label="New leads" value={stats.newLeads} />
        <Kpi label="MQLs" value={stats.mqls} />
        <Kpi label="SQLs" value={stats.sqls} />
        <Kpi label="Meetings" value={stats.meetings} />
        <Kpi label="Proposals" value={stats.proposals} />
        <Kpi label="Won deals" value={stats.won} />
        <Kpi label="Lost deals" value={stats.lost} />
        <Kpi label="Win rate" value={`${stats.winRate}%`} />
        <Kpi label="Pipeline value" value={`₹${stats.pipelineValue.toLocaleString("en-IN")}`} />
        <Kpi label="Weighted pipeline" value={`₹${Math.round(stats.weightedPipeline).toLocaleString("en-IN")}`} />
        <Kpi label="Avg deal size" value={`₹${Math.round(stats.avgDealSize).toLocaleString("en-IN")}`} />
        <Kpi label="Sales cycle" value={stats.avgCycle !== null ? `${stats.avgCycle} days` : "-"} />
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-hidden mb-5">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <span className="font-semibold text-sm text-foreground">Funnel, current snapshot</span>
          <span className="font-mono text-[11px] text-muted-foreground">
            Pipeline coverage: {stats.pipelineCoverage !== null ? `${stats.pipelineCoverage}% of revenue target` : "no target set"}
          </span>
        </div>
        <div className="p-5">
          {FUNNEL_STAGES.map((s, i) => (
            <div key={s} className="flex items-center gap-3 mb-2.5 last:mb-0">
              <div className="w-24 text-xs text-muted-foreground">{STAGE_LABELS[s]}</div>
              <div className="flex-1 h-6 bg-accent rounded-md overflow-hidden">
                <div
                  className={`h-full rounded-md ${s === "won" ? "bg-primary" : "bg-primary/70"}`}
                  style={{ width: `${Math.max(4, Math.round((funnelCounts[i] / maxFunnel) * 100))}%` }}
                />
              </div>
              <div className="w-8 text-right font-mono text-xs text-muted-foreground">{funnelCounts[i]}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <span className="font-semibold text-sm text-foreground">Deals</span>
          <button
            onClick={() => setShowAdd(true)}
            className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold"
          >
            + Add deal
          </button>
        </div>
        {deals.length === 0 ? (
          <p className="text-sm text-muted-foreground p-5">No deals yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3">Deal</th>
                <th className="text-left font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3">Stage</th>
                <th className="text-right font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3">Value</th>
                <th className="text-left font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3">Expected close</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => (
                <tr key={d.id} className="border-b border-border last:border-0">
                  <td className="px-5 py-3">
                    <div className="font-medium text-foreground">{d.name}</div>
                    {d.clients?.company_name && <div className="text-xs text-muted-foreground">{d.clients.company_name}</div>}
                  </td>
                  <td className="px-5 py-3">
                    <span className="font-mono text-[10.5px] bg-accent text-foreground rounded-full px-2.5 py-1">
                      {STAGE_LABELS[d.stage]}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">₹{Number(d.value).toLocaleString("en-IN")}</td>
                  <td className="px-5 py-3 text-muted-foreground">{d.expected_close_date || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <AddDealModal
          clients={clients}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border border-border rounded-2xl bg-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{label}</div>
      <div className="text-lg font-semibold text-foreground">{value}</div>
    </div>
  );
}

function AddDealModal({ clients, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [clientId, setClientId] = useState("");
  const [stage, setStage] = useState("lead");
  const [dealType, setDealType] = useState("new_business");
  const [value, setValue] = useState("");
  const [expectedClose, setExpectedClose] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    if (!name.trim() || !value) {
      setError("Name and value are required.");
      return;
    }
    setSaving(true);
    const { error: insertError } = await supabase.from("deals").insert({
      name: name.trim(),
      client_id: clientId || null,
      stage,
      deal_type: dealType,
      value: parseFloat(value),
      expected_close_date: expectedClose || null,
    });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    onSaved();
  }

  return (
    <ModalShell title="Add deal" onClose={onClose}>
      <ErrorBanner error={error} />
      <Field label="Deal name">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Coastal Furnishings, retainer" />
      </Field>
      <Field label="Client (optional, for a prospect not yet a client)">
        <SelectInput value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Not linked to an existing client</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.company_name}
            </option>
          ))}
        </SelectInput>
      </Field>
      <Field label="Stage">
        <SelectInput value={stage} onChange={(e) => setStage(e.target.value)}>
          {STAGES.map((s) => (
            <option key={s} value={s}>
              {STAGE_LABELS[s]}
            </option>
          ))}
        </SelectInput>
      </Field>
      <Field label="Deal type" hint="Upsell/cross-sell only make sense for an existing client">
        <SelectInput value={dealType} onChange={(e) => setDealType(e.target.value)} disabled={!clientId}>
          <option value="new_business">New business</option>
          <option value="upsell">Upsell</option>
          <option value="cross_sell">Cross-sell</option>
        </SelectInput>
      </Field>
      <Field label="Value">
        <TextInput type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="300000" />
      </Field>
      <Field label="Expected close date">
        <TextInput type="date" value={expectedClose} onChange={(e) => setExpectedClose(e.target.value)} />
      </Field>
      <ModalFooter onClose={onClose} onSave={handleSave} saving={saving} />
    </ModalShell>
  );
}
